import { ManagedSource } from '../types';

// COM-647 Task 4: pure request-state logic for SourcesSettingsPage.tsx,
// mirroring how feedFilters.ts already separates AllArticlesPage's own
// filter state from its component - same idea, adapted to the Sources
// Settings table's own params (`SourcesContext#fetchManagedSources`) rather
// than the feed's.

/** Figma draws a "Status" filter over Active/Inactive - `'both'` (the
 * default) sends no `monitored` param at all, matching
 * `fetchManagedSources`'s own tri-state contract (`sourceListQuery.ts`:
 * `null`/absent = no filter). */
export type SourcesStatusFilter = 'both' | 'active' | 'inactive';

export interface SourcesDateRange {
  from: Date | null;
  to: Date | null;
}

export interface SourcesTableFilters {
  search: string;
  statusFilter: SourcesStatusFilter;
  dateRange: SourcesDateRange;
  sortBy: 'name' | 'createdAt';
  sortDir: 'asc' | 'desc';
}

// `sortBy: 'createdAt', sortDir: 'desc'` matches `sourceListQuery.ts`'s own
// backend-mirrored default, so an untouched page's first fetch is identical
// to one that explicitly asked for "newest first".
export const DEFAULT_SOURCES_TABLE_FILTERS: SourcesTableFilters = {
  search: '',
  statusFilter: 'both',
  dateRange: { from: null, to: null },
  sortBy: 'createdAt',
  sortDir: 'desc',
};

export interface SourcesTableFetchParams {
  search?: string;
  monitored?: boolean;
  dateFrom?: string;
  dateTo?: string;
  sortBy: 'name' | 'createdAt';
  sortDir: 'asc' | 'desc';
}

// UTC calendar day - same extraction `feedFilters.ts`'s own `toDateOnly`
// uses for the All Articles date filter, matching how the backend stores/
// compares `createdAt` (see that function's own comment for the one
// documented custom-range caveat, which applies here identically).
function toDateOnly(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Converts the page's own filter state into `fetchManagedSources`'s params.
 * The untouched default state produces an object with ONLY `sortBy`/`sortDir`
 * - no `search`/`monitored`/`dateFrom`/`dateTo` key at all - so a fresh page
 * load asks for exactly what the backend's own default already returns. */
export function toSourcesTableParams(filters: SourcesTableFilters): SourcesTableFetchParams {
  const params: SourcesTableFetchParams = { sortBy: filters.sortBy, sortDir: filters.sortDir };

  const trimmedSearch = filters.search.trim();
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }

  if (filters.statusFilter === 'active') {
    params.monitored = true;
  } else if (filters.statusFilter === 'inactive') {
    params.monitored = false;
  }

  if (filters.dateRange.from) {
    params.dateFrom = toDateOnly(filters.dateRange.from);
  }
  if (filters.dateRange.to) {
    params.dateTo = toDateOnly(filters.dateRange.to);
  }

  return params;
}

export type SourcesToolbarAction = 'add' | 'delete';

/** Selection -> toolbar rule (Confluence scenario #7): any selection shows
 * "Delete Sources"; an empty selection shows "+ Add source" - including
 * right after the LAST checkbox is cleared, which must restore "Add source"
 * rather than leaving "Delete Sources" stuck on screen. */
export function sourcesToolbarAction(selectedCount: number): SourcesToolbarAction {
  return selectedCount > 0 ? 'delete' : 'add';
}

// COM-647 design doc ("What 'Invalid source' means"): the red alert badge
// with its "Invalid source" tooltip does NOT mean the url failed format
// validation (the add/edit modals already reject that before saving) - it
// means the source was scanned and the scan failed: `status === 'error'`.
// This overlaps COM-944 (unified fetch, LLM fallback, a truer "unreadable"
// signal); when that lands it should only ever need to change the ONE
// line below, not a search-and-replace across every render site that draws
// the badge - so every "is this row Invalid?" check must route through this
// function rather than re-testing `status` inline.
export function isInvalidSource(source: Pick<ManagedSource, 'status'>): boolean {
  return source.status === 'error';
}
